/* eslint-disable react/prop-types */
/* eslint-disable arrow-body-style */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Grid2 as Grid, Typography, Box, Divider, Stack, Button } from '@mui/material';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { SignView } from './SignView';
import SignModal from './SignModal';
import useAuth from 'hooks/useAuth';
import usePermission from 'hooks/usePermission';
import { useGetClientLogoQuery } from 'store/services/client';

export const TimeEntryPrintContent = forwardRef(({ data, logo, clientName, imageError, handleImageError, headerAction }, ref) => {
    const totalTime = data?.time_entry_details_time_entry?.reduce((sum, item) => sum + (Number(item?.minutes) || 0), 0) || 0;

    return (
        <Box ref={ref}>
            <Stack
                spacing={2}
                sx={{
                    mb: 2,
                    '@media print': {
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        '@media print': {
                            width: '100%'
                        }
                    }}
                >
                    {logo && !imageError ? (
                        <Box
                            component="img"
                            src={logo}
                            alt={clientName}
                            onError={handleImageError}
                            sx={{
                                width: 132,
                                height: 48,
                                objectFit: 'contain',
                                objectPosition: 'center center',
                                flexShrink: 0,
                                '@media print': {
                                    objectPosition: 'center center'
                                }
                            }}
                        />
                    ) : (
                        <Typography variant="h4" sx={{ fontWeight: 600, flexShrink: 0 }}>
                            {clientName}
                        </Typography>
                    )}
                </Box>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        '@media print': {
                            justifyContent: 'center'
                        }
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            lineHeight: 1.2
                        }}
                    >
                        Time Sheet Details
                    </Typography>
                    {headerAction && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', '@media print': { display: 'none' } }}>{headerAction}</Box>
                    )}
                </Stack>
            </Stack>
            <Box
                sx={{
                    p: 3,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    '@media print': {
                        backgroundColor: 'transparent'
                    }
                }}
            >
                <Grid container spacing={2}>
                    {/* Basic Info */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2">
                            <strong>Location:</strong> {data?.location?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2">
                            <strong>Therapist:</strong> {data?.therapist?.name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2">
                            <strong>Service:</strong> {data?.service?.service_name || '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2">
                            <strong>Date:</strong> {data?.date ? dayjs(data.date).format('MM/DD/YYYY') : '-'}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Time Info */}
                <Grid container spacing={2}>
                    {/* <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                        <strong>Start Time:</strong> {data?.start_time ? moment.utc(data.start_time).local().format('hh:mm A') : '-'}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2">
                        <strong>End Time:</strong> {data?.end_time ? moment.utc(data.end_time).local().format('hh:mm A') : '-'}
                    </Typography>
                </Grid> */}
                    <Grid size={12}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'bold'
                            }}
                        >
                            Total Duration: {totalTime} minutes
                        </Typography>
                    </Grid>
                </Grid>

                {/* Additional Activities */}
                {data?.time_entry_details_time_entry?.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Additional Activities
                        </Typography>
                        <Stack spacing={1}>
                            {data?.time_entry_details_time_entry?.map((item, index) => (
                                <Box key={index} sx={{ pl: 1 }}>
                                    <Typography variant="body2">
                                        <strong>{index + 1}.</strong> <strong>Activity:</strong> {item.activity_log_setup?.name || '-'} |{' '}
                                        <strong>Count:</strong> {item?.num_of_activity} | <strong>Time:</strong> {item?.minutes} minutes{' '}
                                        {item?.campus?.name && (
                                            <>
                                                | <strong>Campus:</strong> {item.campus.name}
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}

                <Grid>
                    <SignView data={data} parent="view" />
                </Grid>
            </Box>
        </Box>
    );
});

TimeEntryPrintContent.displayName = 'TimeEntryPrintContent';

const TimeEntryView = ({ data, onSignSuccess }) => {
    const [showSign, setShowSign] = useState(false);
    const [imageError, setImageError] = useState(false);
    const printRef = useRef();
    const { user, client } = useAuth();
    const { hasPermission } = usePermission();
    const { data: logo } = useGetClientLogoQuery({ ...(user?.client_id && { client_id: user.client_id }) }, { skip: !user?.client_id });

    const clientName = client?.name || data?.client?.name || '-';
    const signedTherapistId = typeof data?.signed_therapist_emp === 'object' ? data?.signed_therapist_emp?.id : data?.signed_therapist_emp;
    const isSigned = data?.is_full_signed || data?.is_signed_by_therapist;
    const canSign =
        data?.id &&
        hasPermission(['time_entry_sign']) &&
        data?.therapist?.id === user?.emp_id &&
        !isSigned &&
        signedTherapistId !== user?.emp_id;

    const handleHideSign = () => {
        setShowSign(false);
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current
    });

    const handleImageError = () => {
        setImageError(true);
    };

    const handleUpdate = async () => {
        return true;
    };

    useEffect(() => {
        setImageError(false);
    }, [logo, user?.client_id]);

    return (
        <>
            <TimeEntryPrintContent
                ref={printRef}
                data={data}
                logo={logo}
                clientName={clientName}
                imageError={imageError}
                handleImageError={handleImageError}
                headerAction={
                    <Button variant="contained" color="primary" onClick={handlePrint} sx={{ minWidth: 92 }}>
                        Print
                    </Button>
                }
            />
            {canSign && (
                <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-start' }}>
                    <Button variant="contained" color="primary" onClick={() => setShowSign(true)} sx={{ minWidth: 104 }}>
                        E-sign
                    </Button>
                </Stack>
            )}
            <SignModal
                open={showSign}
                handleClose={handleHideSign}
                id={data?.id}
                handleUpdate={handleUpdate}
                onSuccess={onSignSuccess}
                restrictPasswordAutoPopulate
            />
        </>
    );
};

export default TimeEntryView;
